import hotkeys from 'hotkeys-js';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { setButtonTypeClickAction, setUploadFromAction } from 'reactApp/modules/upload/upload.module';
import { Button } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import Dialog from 'reactApp/ui/Dialog/Dialog';
import { UPLOAD_DIALOG_ID } from 'reactApp/ui/UploadDialog/UploadDialog.constants';
import { noop } from 'reactApp/utils/helpers';

import styles from './UploadDialog.css';

export interface IProps {
    onClose: (event?: MouseEvent) => void;
    folderId: string;
    folderHome: string;
    folderName: string;
    setInputs: (input: HTMLInputElement | null, onClose) => void;
    openFrom?: string;
}

export interface IPropsCommon {
    fileSizeLimit: string;
    allowMultiple: boolean;
    showIncreaseLink?: boolean;
}

const HOT_KEY_SCOPE_NAME = 'UploadDialog';

export const UploadDialog = memo(
    ({
        folderId,
        folderHome,
        allowMultiple,
        folderName,
        showIncreaseLink = true,
        fileSizeLimit,
        setInputs = noop,
        onClose = noop,
        openFrom = 'upldlg',
    }: IProps & IPropsCommon) => {
        const dispatch = useDispatch();

        const inputRef = useRef<HTMLInputElement | null>(null);

        if (folderId === ROOT_FOLDER_ID && folderHome === ROOT_FOLDER_ID) {
            folderName = 'Облако';
        }

        const handleClose = useCallback(
            (event) => {
                if (event) {
                    event.stopPropagation();
                    event.nativeEvent?.stopImmediatePropagation?.();
                }
                onClose(event);
            },
            [onClose]
        );

        const handleOnChange = useCallback(
            (event) => {
                dispatch(setButtonTypeClickAction(event.type === 'drop' ? 'dnd' : 'click'));
                onClose(event);
            },
            [onClose]
        );

        useEffect(() => {
            if (openFrom) {
                dispatch(setUploadFromAction(openFrom));
            }
        }, []);

        useHotKeyScope(HOT_KEY_SCOPE_NAME);

        useEffect(() => {
            setInputs(inputRef.current, handleClose);
            const openDialog = () => inputRef.current?.click();

            document.addEventListener('drop', handleOnChange, true);
            hotkeys('enter', HOT_KEY_SCOPE_NAME, openDialog);

            return () => {
                document.removeEventListener('drop', handleOnChange, true);
                hotkeys.unbind('enter', HOT_KEY_SCOPE_NAME, openDialog);
            };
        }, [setInputs, handleClose, handleOnChange]);

        const renderFooter = () => {
            return (
                <div className={styles.footer}>
                    <div className={styles.buttonWrapper}>
                        <Button data-name="confirm" theme="octavius" fluid primary>
                            Выбрать файлы
                        </Button>
                        <input
                            type="file"
                            name="files"
                            ref={inputRef}
                            className={styles.input}
                            multiple={allowMultiple}
                            onChange={handleOnChange}
                        />
                    </div>
                    <div className={styles.info}>
                        <div className={styles.text}>Максимальный размер файла {fileSizeLimit}</div>
                        {showIncreaseLink && (
                            <a className={styles.link} href="/subscriptions" target="_blank">
                                Снять ограничение
                            </a>
                        )}
                    </div>
                </div>
            );
        };

        return (
            <Dialog
                id={UPLOAD_DIALOG_ID}
                open
                dimmer
                size="small"
                className={styles.root}
                header={`Загрузить в ${folderName}`}
                onCancel={handleClose}
                footer={renderFooter()}
            >
                <Content isModal>
                    <div className={styles.content}>
                        Перетащите сюда
                        <br />
                        файлы для загрузки
                    </div>
                </Content>
            </Dialog>
        );
    }
);

UploadDialog.displayName = 'UploadDialog';
